/**
 * Goals — the daily budget, and the weight it should add up to.
 *
 * Two forms because these are two different objects, and the backend keeps
 * them apart on purpose (see `models/goal.py`): a daily target is the calorie
 * and macro budget the ring measures against; a weight goal is the outcome —
 * a weight, by a date, at a rate. Editing one must never silently rewrite the
 * other, so they don't share a save button either.
 *
 * Both are effective-dated server-side. Saving applies from today forward;
 * finished days keep the numbers they were scored against. The copy on each
 * card says so, because "did changing my goal rescore last week?" is the
 * question a user can't answer by experiment without ruining their history.
 *
 * ## Why this is a section and not a page
 *
 * It used to be `/goals`: a route with no tab, reachable only from two links
 * that happened to point at it. That is a destination the navigation does not
 * admit exists — you could set a budget and then be unable to find where you
 * had done it. A goal is a fact about *you*, like the timezone and the saved
 * meals already on **You**, so it belongs there. This file exports the section;
 * `Foods.tsx` renders it, and `/goals` now sends you to the same place.
 */

import {
  ageOn,
  earliestGoalDate,
  estimateBudget,
  impliedRate,
  kcalPerDayFor,
  localDate,
  macroKcal,
  parseDecimal,
  plannedRate,
  useDay,
  useOverview,
  useProfile,
  useSetDailyTarget,
  useSetGoal,
  validateDailyTarget,
  validateWeightGoal,
  type GoalProgress,
  type Macros,
  type Overview,
  type Profile,
} from "@calorio/core";
import { useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { useCalorio } from "../app/providers";
import { EstimatedBudget } from "../components/EstimatedBudget";

/**
 * The goals section of the You page.
 *
 * `id="goals"` is the anchor the two links elsewhere aim at — the ring when
 * there is no budget, and the Weight page's cards. Both used to point at a
 * page; they now point at the part of You that answers them.
 */
export function GoalsSection() {
  const ctx = useCalorio();
  const day = useDay(ctx);
  const overview = useOverview(ctx, 30);
  const profile = useProfile(ctx);

  if (day.isLoading || overview.isLoading || profile.isLoading)
    return (
      <section id="goals">
        <p className="muted">Loading…</p>
      </section>
    );
  if (!day.data || !profile.data)
    return (
      <section id="goals">
        <p className="error">Couldn't load your goals.</p>
      </section>
    );

  return (
    <section id="goals" className="stack">
      <div>
        <h2 style={{ fontSize: "var(--step-1)", margin: 0 }}>Goals</h2>
        <p className="stat__label" style={{ margin: 0 }}>
          The daily budget the ring measures against, and the weight it should add up to.
        </p>
      </div>

      <div className="grid grid--halves" style={{ alignItems: "start" }}>
        {/* The profile and the overview both already live here, and the budget
            card needs them to work a number out rather than ask for one. They
            are passed down rather than queried again inside the card so this
            section's own exported signature stays argument-free — `You.tsx`
            renders it as `<GoalsSection />` and should not have to learn. */}
        <BudgetCard
          current={day.data.target}
          profile={profile.data}
          overview={overview.data}
        />
        {/* `?? null` on the weigh-in because the field is optional in the
            schema, not merely nullable: it is derived from the weight series
            rather than stored, so a payload without it is a valid answer
            meaning "never weighed". The card's own guard tests for null. */}
        <WeightGoalCard
          goal={overview.data?.goal ?? null}
          latestWeighIn={profile.data.latest_weight_kg ?? null}
        />
      </div>
    </section>
  );
}

/**
 * The calorie and macro budget, effective from today.
 *
 * Macros default to zero rather than blocking, same reasoning as manual food
 * entry: someone may know the calorie budget they want and genuinely not have
 * macro targets, and refusing the save over a blank protein field helps nobody.
 *
 * ## Why it offers to work the number out
 *
 * Four boxes asking for a calorie figure and three macro grams *is* the hand
 * arithmetic the first-run screen exists to replace. Someone who skipped setup,
 * or whose setup computed nothing, followed "Set a daily budget →" here and
 * found the very thing they were spared. The profile already carries weight,
 * height, date of birth and activity level, so the estimate is a derivation and
 * not a question.
 *
 * ## Why "Use this estimate" fills the fields instead of saving
 *
 * Three reasons, and they compound. This card's whole shape — the Atwater
 * cross-check, the "in force from today" caveat, a Save button that says
 * "Update" when there is something to update — is about *reviewing* a number
 * before committing to it. Unlike on first run a budget may already be in
 * force, and a one-tap effective-dated overwrite of it is destructive in a way
 * a one-tap first budget is not. And it keeps `submit` → `validateDailyTarget`
 * → `save.mutate` as the only path that writes, so there is one place where a
 * budget can be wrong rather than two.
 *
 * `type="button"` on both the offer and the action, because they sit inside
 * this card's `<form>` and a default submit would save the *old* field values —
 * the precise opposite of what the tap means.
 */
function BudgetCard({
  current,
  profile,
  overview,
}: {
  current: Macros | null;
  profile: Profile;
  overview: Overview | undefined;
}) {
  const ctx = useCalorio();
  const save = useSetDailyTarget(ctx);
  const today = localDate(ctx.timeZone);

  const [kcal, setKcal] = useState(current ? String(Math.round(current.kcal)) : "");
  const [protein, setProtein] = useState(current ? String(Math.round(current.protein_g)) : "");
  const [carbs, setCarbs] = useState(current ? String(Math.round(current.carbs_g)) : "");
  const [fat, setFat] = useState(current ? String(Math.round(current.fat_g)) : "");
  const [problem, setProblem] = useState<string | null>(null);
  // Open by default only for the empty case, which is the hand-arithmetic trap
  // being fixed. With a budget already in force the readout would be an
  // unprompted second opinion sitting over numbers the user chose.
  const [offered, setOffered] = useState(current === null);

  // Typing anything drops the stale "Saved" note — otherwise it sits there
  // asserting that whatever is now in the fields is what was saved.
  const edit = (set: (v: string) => void) => (value: string) => {
    set(value);
    save.reset();
  };

  // The Atwater cross-check, shared with the phone so the two can't disagree
  // about whether a budget adds up. The macro grams and the calorie figure are
  // entered independently and people expect them to agree; showing what the
  // macros sum to catches a typo before it becomes a week of wrong rings.
  const supplied = macroKcal({
    protein_g: parseDecimal(protein) || 0,
    carbs_g: parseDecimal(carbs) || 0,
    fat_g: parseDecimal(fat) || 0,
  });
  const kcalValue = parseDecimal(kcal);
  const diverges =
    supplied !== null &&
    Number.isFinite(kcalValue) &&
    kcalValue > 0 &&
    Math.abs(supplied - kcalValue) / kcalValue > 0.05;

  /*
   * The estimate, guarded inline rather than behind an early return.
   *
   * Every hook above has already run and must keep running: the smoke test
   * mounts **You** with `birth_date: null` and `height_cm: null`, so a `return`
   * before `useState` on the incomplete profile would be a conditional hook —
   * which typechecks, renders white, and takes that whole file red with it.
   */
  const age = profile.birth_date ? ageOn(profile.birth_date, today) : null;
  const weighIn = profile.latest_weight_kg ?? null;
  /*
   * Reading the goal's rate is safe here, and the next reader will want to
   * "fix" it: `WeightGoalCard`'s docstring warns that
   * `target_rate_kg_per_week` is stored-*or-derived* and must not be echoed
   * back. That warning is about putting it in a **form field**, where the next
   * save would store a derived number as a cap. Sizing a budget with it writes
   * nothing at all, and a budget computed at maintenance for somebody actively
   * losing weight would be the wrong number.
   *
   * Which is what `?? 0` here used to produce. A target weight with no date
   * has no stored rate and nothing to derive one from, so this card offered
   * maintenance to the very person the paragraph above says it must not — and
   * disagreed by 550 kcal with the budget first run had already set them from
   * the same facts. `plannedRate` is that default, in one place.
   */
  const goal = overview?.goal ?? null;
  const rate = goal
    ? plannedRate({
        targetKg: goal.target_weight_kg,
        latestWeighIn: weighIn ?? NaN,
        today,
        date: goal.target_date ?? "",
        rate: goal.target_rate_kg_per_week != null ? String(goal.target_rate_kg_per_week) : "",
      })
    : 0;
  const estimate =
    weighIn !== null && profile.height_cm !== null && age !== null
      ? estimateBudget({
          weightKg: weighIn,
          heightCm: profile.height_cm,
          ageYears: age,
          sex: profile.sex,
          activity: profile.activity_level,
          rateKgPerWeek: rate,
        })
      : null;

  /*
   * Everything missing, named at once and in one sentence.
   *
   * One at a time would be a "fix it, get told about the next one" loop, which
   * is the shape of a form that resents being filled in. The weigh-in gets a
   * real link because it lives on another page; height and date of birth get
   * words, because `ProfileCard` renders immediately under this section on the
   * same page — "directly below" is literally true, and a `/you#profile` link
   * would satisfy the route test and then scroll nowhere, since that card has
   * no `id` and the hash effect on **You** handles only `#goals`.
   */
  const missing: ReactNode[] = [];
  if (weighIn === null)
    missing.push(
      <Link key="weigh-in" to="/weight" className="muted">
        a weigh-in
      </Link>,
    );
  if (profile.height_cm === null) missing.push("your height");
  if (age === null) missing.push("your date of birth");
  const inProfile = profile.height_cm === null || age === null;
  const listed = missing.flatMap((item, index) =>
    index === 0 ? [item] : [index === missing.length - 1 ? " and " : ", ", item],
  );

  /** Fill the four fields from the estimate. Writing is still Save's job. */
  const fill = (from: NonNullable<typeof estimate>) => () => {
    setKcal(String(from.budgetKcal));
    setProtein(String(from.protein_g));
    setCarbs(String(from.carbs_g));
    setFat(String(from.fat_g));
    setProblem(null);
    // Otherwise a "Saved — in force from today" note from an earlier save sits
    // over four numbers that have never been saved.
    save.reset();
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setProblem(null);

    const checked = validateDailyTarget({ kcal, protein, carbs, fat });
    if (!checked.ok) return setProblem(checked.problem);

    // `effective_from` deliberately omitted: the server anchors it to the
    // user's today, in the profile's timezone — the one fact it knows better
    // than a device that may have just landed somewhere.
    save.mutate(checked.value);
  };

  return (
    <form className="card stack" style={{ gap: "var(--sp-3)" }} onSubmit={submit}>
      <div>
        <h2 className="card__title" style={{ marginBottom: 0 }}>
          Daily budget
        </h2>
        {current && (
          <p className="stat__label" style={{ margin: "var(--sp-1) 0 0" }}>
            A new budget applies from today; finished days keep the one they were scored against.
          </p>
        )}
      </div>

      {estimate ? (
        offered ? (
          <EstimatedBudget
            estimate={estimate}
            inset
            label="Worked out from your profile"
            footnote="Nothing is saved until you press the button at the bottom of this card."
          >
            <button type="button" className="budget__use" onClick={fill(estimate)}>
              Use this estimate
            </button>
          </EstimatedBudget>
        ) : (
          <button type="button" className="budget__offer" onClick={() => setOffered(true)}>
            Work it out from my profile
          </button>
        )
      ) : (
        <p className="stat__label" style={{ margin: 0 }}>
          Calorio can work this out for you — it needs {listed}
          {inProfile ? ", and Profile is directly below." : "."}
        </p>
      )}

      <label className="manual__row">
        <span>Calories</span>
        <input
          className="field num manual__value"
          inputMode="numeric"
          value={kcal}
          onChange={(e) => edit(setKcal)(e.target.value)}
          placeholder="2000"
          aria-label="Daily calorie budget"
          required
        />
      </label>

      {(
        [
          ["Protein", protein, setProtein],
          ["Carbs", carbs, setCarbs],
          ["Fat", fat, setFat],
        ] as const
      ).map(([label, value, set]) => (
        <label className="manual__row" key={label}>
          <span>
            {label}
            <span className="muted"> g</span>
          </span>
          <input
            className="field num manual__value"
            inputMode="numeric"
            value={value}
            onChange={(e) => edit(set)(e.target.value)}
            placeholder="0"
            aria-label={`Daily ${label.toLowerCase()} target in grams`}
          />
        </label>
      ))}

      {supplied !== null && (
        <p className="stat__label" style={{ margin: 0 }}>
          these macros supply ≈ <span className="num">{supplied.toLocaleString()}</span> kcal
          {diverges && " — the ring counts the calorie figure"}
        </p>
      )}

      {problem && (
        <p className="error" style={{ margin: 0 }}>
          {problem}
        </p>
      )}
      {save.isError && (
        <p className="error" style={{ margin: 0 }}>
          Couldn't save the budget. {save.error?.message ?? "Try again."}
        </p>
      )}
      {save.isSuccess && (
        <p className="muted" style={{ margin: 0 }}>
          Saved — in force from today.
        </p>
      )}

      <button className="button" type="submit" disabled={save.isPending}>
        {save.isPending ? "Saving…" : current ? "Update budget" : "Set budget"}
      </button>
    </form>
  );
}

/**
 * The long-term goal: a weight, optionally by a date, optionally rate-capped.
 *
 * The rate field is never prefilled, even when the current goal has one to
 * show. `GoalProgress.target_rate_kg_per_week` is stored-*or-derived* — the
 * server computes it from the target and date when none was set — and echoing
 * a derived value back into the field would quietly store it as a cap on the
 * next save. Blank means "derive from the date", and stays blank.
 */
function WeightGoalCard({
  goal,
  latestWeighIn,
}: {
  goal: GoalProgress | null;
  latestWeighIn: number | null;
}) {
  const ctx = useCalorio();
  const save = useSetGoal(ctx);
  const today = localDate(ctx.timeZone);

  const [target, setTarget] = useState(goal ? String(goal.target_weight_kg) : "");
  const [date, setDate] = useState(goal?.target_date ?? "");
  const [rate, setRate] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  const edit = (set: (v: string) => void) => (value: string) => {
    set(value);
    save.reset();
  };

  // A goal needs a starting point, and the server snapshots it from the latest
  // weigh-in. No weigh-in, no form — a disabled save with no way to enable it
  // is a dead end, so this points at the thing to do instead.
  if (latestWeighIn === null) {
    return (
      <div className="card">
        <h2 className="card__title">Weight goal</h2>
        <p style={{ margin: 0 }}>
          A goal needs a starting point.{" "}
          <Link to="/weight" className="muted">
            Log a weigh-in first →
          </Link>
        </p>
      </div>
    );
  }

  // What the plan implies, computed the moment there's enough to compute it.
  // Shown with its ≈ kcal/day equivalent, because "−0.4 kg a week" and "440
  // kcal below maintenance every day" are the same plan felt differently.
  const implied = impliedRate({
    targetKg: parseDecimal(target),
    latestWeighIn,
    today,
    date,
    rate,
  });
  const showImplied = implied !== null && Math.abs(implied) >= 0.005;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setProblem(null);

    const checked = validateWeightGoal({ target, date, rate, latestWeighIn, today });
    if (!checked.ok) return setProblem(checked.problem);
    save.mutate(checked.value);
  };

  return (
    <form className="card stack" style={{ gap: "var(--sp-3)" }} onSubmit={submit}>
      <div>
        <h2 className="card__title" style={{ marginBottom: 0 }}>
          Weight goal
        </h2>
        {goal && (
          <p className="stat__label" style={{ margin: "var(--sp-1) 0 0" }}>
            <span className="num">Day {goal.day}</span>
            {goal.total_days != null && <span className="num"> of {goal.total_days}</span>}
            {" · "}
            <span className="num">{goal.start_weight_kg.toFixed(1)}</span> →{" "}
            <span className="num">{goal.target_weight_kg.toFixed(1)} kg</span>
            {goal.achieved && " · reached"}
          </p>
        )}
        <p className="stat__label" style={{ margin: "var(--sp-1) 0 0" }}>
          starts from your latest weigh-in ·{" "}
          <span className="num">{latestWeighIn.toFixed(1)} kg</span>
        </p>
      </div>

      <label className="manual__row">
        <span>
          Target weight
          <span className="muted"> kg</span>
        </span>
        <input
          className="field num manual__value"
          inputMode="decimal"
          value={target}
          onChange={(e) => edit(setTarget)(e.target.value)}
          placeholder="79.5"
          aria-label="Target weight in kilograms"
          required
        />
      </label>

      <label className="manual__row">
        <span>
          By
          <span className="muted"> (optional)</span>
        </span>
        <input
          className="field num"
          type="date"
          value={date}
          min={earliestGoalDate(today)}
          onChange={(e) => edit(setDate)(e.target.value)}
          aria-label="Target date"
          style={{ width: 170 }}
        />
      </label>

      <label className="manual__row">
        <span>
          Weekly rate
          <span className="muted"> kg, − to lose</span>
        </span>
        <input
          className="field num manual__value"
          inputMode="decimal"
          value={rate}
          onChange={(e) => edit(setRate)(e.target.value)}
          placeholder={date ? "from date" : "−0.5"}
          aria-label="Target rate in kilograms per week, negative to lose"
        />
      </label>

      {showImplied && (
        <p className="stat__label" style={{ margin: 0 }}>
          ≈ <span className="num">{implied.toFixed(2)}</span> kg/week ·{" "}
          <span className="num">
            {Math.abs(Math.round(kcalPerDayFor(implied))).toLocaleString()}
          </span>{" "}
          kcal/day {implied < 0 ? "below" : "above"} maintenance
        </p>
      )}

      {goal && !goal.achieved && (
        <p className="stat__label" style={{ margin: 0 }}>
          Saving starts a fresh goal from today; the current one stays in your history.
        </p>
      )}

      {problem && (
        <p className="error" style={{ margin: 0 }}>
          {problem}
        </p>
      )}
      {save.isError && (
        <p className="error" style={{ margin: 0 }}>
          Couldn't save the goal. {save.error?.message ?? "Try again."}
        </p>
      )}
      {save.isSuccess && (
        <p className="muted" style={{ margin: 0 }}>
          Goal set — day 1 is today.
        </p>
      )}

      <button className="button" type="submit" disabled={save.isPending}>
        {save.isPending ? "Saving…" : goal ? "Start new goal" : "Set goal"}
      </button>
    </form>
  );
}
